/**
 * Easy Pets — shared back-in-stock notify modal.
 *
 * Opens from any [data-notify-open] control on sold-out product cards, pre-fills
 * the product, and submits the native Shopify contact form into a hidden iframe
 * so Shopify actually sends the email without reloading the page.
 *
 * Uses a custom overlay (not <dialog>.showModal()) so it works inside the
 * Shopify theme-editor's sandboxed iframe, which blocks native modal dialogs.
 * All handlers are arrow-function fields so they bind correctly and can be
 * added/removed as listeners.
 */
class EasypetsNotifyModal extends HTMLElement {
  #lastTrigger = null;
  #pendingSubmit = false;

  connectedCallback() {
    this.overlay = this.querySelector('[data-notify-overlay]');
    this.panel = this.querySelector('[data-notify-panel]');
    this.form = this.querySelector('form');
    this.sink = this.querySelector('iframe[name="easypets-notify-sink"]');
    this.body = this.querySelector('[data-notify-body]');
    this.success = this.querySelector('[data-notify-success]');
    this.productLabel = this.querySelector('[data-notify-product]');
    this.productField = this.querySelector('[data-notify-field]');
    this.emailInput = this.querySelector('input[type="email"]');

    if (!this.overlay) return;

    document.addEventListener('click', this.#onOpenClick);
    this.querySelector('[data-notify-close]')?.addEventListener('click', this.#close);
    this.overlay.addEventListener('click', this.#onOverlayClick);
    document.addEventListener('keydown', this.#onKeydown);
    this.form?.addEventListener('submit', this.#onSubmit);
    this.sink?.addEventListener('load', this.#onSinkLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onOpenClick);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  get #isOpen() {
    return !this.overlay.hidden;
  }

  /** @param {MouseEvent} event */
  #onOpenClick = (event) => {
    const trigger = event.target.closest('[data-notify-open]');
    if (!trigger) return;
    event.preventDefault();
    this.#open(trigger);
  };

  /** @param {HTMLElement} trigger */
  #open(trigger) {
    this.#lastTrigger = trigger;

    const title = trigger.dataset.notifyTitle || 'this product';
    const url = trigger.dataset.notifyUrl || '';

    // Reset to the form view (in case it was left on success from a prior open).
    if (this.body) this.body.hidden = false;
    if (this.success) this.success.hidden = true;
    if (this.productLabel) this.productLabel.textContent = title;
    if (this.productField) this.productField.value = `${title} — ${window.location.origin}${url}`;

    this.overlay.hidden = false;
    // Force a reflow so the open transition runs, then flag open state.
    void this.overlay.offsetWidth;
    this.overlay.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';

    this.emailInput?.focus();
  }

  #close = () => {
    if (!this.#isOpen) return;
    this.overlay.classList.remove('is-open');
    this.overlay.hidden = true;
    document.documentElement.style.overflow = '';
    this.#lastTrigger?.focus?.();
  };

  /** Close when clicking the backdrop (outside the panel). */
  #onOverlayClick = (event) => {
    if (event.target === this.overlay) this.#close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.#isOpen) return;
    if (event.key === 'Escape') {
      this.#close();
      return;
    }
    if (event.key === 'Tab') this.#trapFocus(event);
  };

  /** Keep keyboard focus inside the panel while open. */
  #trapFocus(event) {
    const focusable = this.panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Native submit goes to the hidden iframe; just flag it and show a sending state. */
  #onSubmit = () => {
    this.#pendingSubmit = true;
    const submit = this.form.querySelector('[type="submit"]');
    if (submit) {
      submit.disabled = true;
      submit.dataset.label = submit.textContent;
      submit.textContent = 'Sending…';
    }
  };

  /** The hidden iframe finishes loading after a real submit → show success. */
  #onSinkLoad = () => {
    if (!this.#pendingSubmit) return; // ignore the initial about:blank load
    this.#pendingSubmit = false;

    const submit = this.form.querySelector('[type="submit"]');
    if (submit) {
      submit.disabled = false;
      if (submit.dataset.label) submit.textContent = submit.dataset.label;
    }
    this.form.reset();
    if (this.body) this.body.hidden = true;
    if (this.success) this.success.hidden = false;
  };
}

if (!customElements.get('easypets-notify-modal')) {
  customElements.define('easypets-notify-modal', EasypetsNotifyModal);
}
